import type { FetchMatcher, JsonApiDoc } from 'tio-data/demo/types';
import type { DemoSessionSelectionSnapshot } from 'tio-employee/utils/career-pathways-demo-data';

const TERMS = [
  { id: 'demo-condition-usage', classification: 'app_terms_of_use', filename: 'terms_of_use.json' },
  {
    id: 'demo-condition-privacy',
    classification: 'app_privacy_policy',
    filename: 'privacy_policy.json',
  },
] as const;
const acceptedConditions = new Set<string>();

/** Forgets simulated term acceptance; a new page load starts with outstanding First Login terms. */
export function resetDemoRegistration() {
  acceptedConditions.clear();
}

/** Other personas have accepted terms; First Login owes both conditions until locally accepted. */
export function demoRegistrationTermsAccepted(selection: DemoSessionSelectionSnapshot) {
  return (
    selection.roleId !== 'employee' ||
    selection.userScenarioId !== 'first-login' ||
    TERMS.every((term) => acceptedConditions.has(term.id))
  );
}

/** The existing registration dialog reads the shipped terms assets for a new employee. */
export function buildDemoRegistrationConditions(
  selection: DemoSessionSelectionSnapshot
): JsonApiDoc {
  return {
    data:
      selection.roleId === 'employee' && selection.userScenarioId === 'first-login'
        ? TERMS.map((term) => ({
            type: 'condition',
            id: term.id,
            attributes: {
              classification: term.classification,
              filename: term.filename,
              province: 'register',
              expiryCadence: 'NEVER',
              isDefault: true,
            },
          }))
        : [],
  };
}

function agreementCondition(body: unknown) {
  const data = (
    body as
      | { data?: { type?: string; relationships?: Record<string, { data?: { id?: string } }> } }
      | undefined
  )?.data;
  if (data?.type !== 'agreement' && data?.type !== 'agreements') {
    return undefined;
  }
  if (data.relationships?.['user']?.data?.id !== 'demo-user') {
    return undefined;
  }
  return TERMS.find((term) => term.id === data.relationships?.['condition']?.data?.id);
}

/** Answers only the fabricated employee's known agreement creates, keeping acceptance local to this page. */
export function buildDemoRegistrationFetchMatchers(
  selection: DemoSessionSelectionSnapshot
): FetchMatcher[] {
  if (selection.roleId !== 'employee' || selection.userScenarioId !== 'first-login') {
    return [];
  }
  return [
    {
      urlMatches: (url) => /\/api\/v2\/agreements(?:\?|$)/.test(url),
      method: 'POST',
      status: 201,
      bodyMatches: (body) => agreementCondition(body) !== undefined,
      json: (_url: string, body: unknown) => {
        const condition = agreementCondition(body);
        if (!condition) {
          return { data: null };
        }
        acceptedConditions.add(condition.id);
        return {
          data: {
            type: 'agreements',
            id:
              condition.classification === 'app_terms_of_use'
                ? 'demo-agreement-usage'
                : 'demo-agreement-privacy',
            attributes: { classification: condition.classification, 'is-expired': false },
          },
        };
      },
    },
  ];
}
